import { useEffect, useMemo, useState } from 'react';

import { Link } from '../router';
import { lifecycle } from '../content';

/**
 * §4.1 Pricing: country selector, currency, monthly/annual toggle, plan comparison, add-ons,
 * taxes and a route to sales.
 *
 * The plans are fetched from the platform's public catalogue rather than written into this file.
 * A price typed into a marketing page drifts the moment somebody changes it in the provider
 * console, and the first person to find out is a customer who was quoted something else at
 * checkout.
 */

interface PublicPlanPrice {
  currency: string;
  interval: string;
  baseAmount: number;
  perSeatAmount: number;
  setupFee: number;
  taxBehavior: string;
}

interface PublicPlan {
  planKey: string;
  name: string;
  description?: string | null;
  tier: string;
  applicationKey?: string | null;
  isBundle: boolean;
  isAddOn: boolean;
  includedSeats: number;
  maximumSeats?: number | null;
  isTrialAvailable: boolean;
  trialDays: number;
  displayOrder: number;
  prices: PublicPlanPrice[];
  features: { displayName: string; limitValue?: number | null }[];
}

interface Catalogue {
  plans: PublicPlan[];
  currencies: string[];
  intervals: string[];
}

/**
 * How each currency is presented, and what the tax on it is called.
 *
 * The selector is built from the currencies the catalogue actually prices, not from this list.
 * Offering a country nobody has priced yet shows a plan in somebody else's money, which is worse
 * than not offering it at all.
 */
const byCurrency: Record<string, { name: string; tax: string }> = {
  SGD: { name: 'Singapore', tax: 'GST' },
  MYR: { name: 'Malaysia', tax: 'SST' },
  INR: { name: 'India', tax: 'GST' },
  IDR: { name: 'Indonesia', tax: 'VAT' },
  USD: { name: 'Elsewhere', tax: 'tax' },
};

export default function Pricing({ navigate }: { navigate: (to: string) => void }) {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [failed, setFailed] = useState(false);

  const [currency, setCurrency] = useState<string | null>(null);
  const [interval, setInterval] = useState('Monthly');

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/v1/public/catalogue', { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data: Catalogue) => setCatalogue(data))
      .catch((error) => {
        if ((error as { name?: string })?.name !== 'AbortError') setFailed(true);
      });

    return () => controller.abort();
  }, []);

  // Default to Singapore where it is priced, otherwise whatever the catalogue leads with.
  const active = currency ?? (catalogue?.currencies.includes('SGD') ? 'SGD' : catalogue?.currencies[0]) ?? 'SGD';
  const region = byCurrency[active] ?? { name: active, tax: 'tax' };

  const priced = useMemo(() => {
    if (!catalogue) return [];

    return catalogue.plans
      .filter((plan) => !plan.isAddOn)
      .map((plan) => ({
        plan,
        price: plan.prices.find((p) => p.currency === active && p.interval === interval),
      }))
      .filter((row) => row.price !== undefined);
  }, [catalogue, active, interval]);

  const addOns = (catalogue?.plans ?? []).filter((plan) => plan.isAddOn);
  const intervals = catalogue?.intervals.length ? catalogue.intervals : ['Monthly', 'Annual'];

  return (
    <>
      <section className="section">
        <div className="wrap">
          <div className="section__head">
            <span className="eyebrow">Pricing</span>
            <h1 style={{ marginBottom: 16 }}>Priced per seat, in your own currency</h1>
            <p>
              Every plan includes the whole platform — identity, companies, security, audit and
              the API. What differs is the application features and how far the limits go.
            </p>
          </div>

          <div className="pricing__controls">
            <label className="control">
              Country
              <select
                value={active}
                onChange={(event) => setCurrency(event.target.value)}
                disabled={!catalogue}
              >
                {(catalogue?.currencies ?? [active]).map((code) => (
                  <option key={code} value={code}>
                    {byCurrency[code]?.name ?? code} ({code})
                  </option>
                ))}
              </select>
            </label>

            <div className="toggle" role="group" aria-label="Billing interval">
              {intervals.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={interval === option}
                  onClick={() => setInterval(option)}
                >
                  {option}
                </button>
              ))}
            </div>

            <span className="control" style={{ marginLeft: 'auto' }}>
              Shown in {active}, {region.tax} added at checkout
            </span>
          </div>

          {failed && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3>Pricing could not be loaded</h3>
              <p>
                The plan catalogue is temporarily unavailable. Talk to us and we will send current
                pricing for {region.name} directly.
              </p>
            </div>
          )}

          {!failed && !catalogue && <p style={{ color: 'var(--muted)' }}>Loading plans…</p>}

          {catalogue && priced.length === 0 && (
            <div className="card">
              <h3>No plan is listed for {active} on a {interval.toLowerCase()} term</h3>
              <p>
                Pricing for {region.name} is set per country. Talk to us and we will quote you
                directly.
              </p>
              <p style={{ marginTop: 16 }}>
                <Link to="/company" onNavigate={navigate} className="btn btn--primary">
                  Talk to sales
                </Link>
              </p>
            </div>
          )}

          {priced.length > 0 && (
            <div className="grid grid--3">
              {priced.map(({ plan, price }, index) => (
                <article
                  key={plan.planKey}
                  className={`plan${index === 1 ? ' plan--featured' : ''}`}
                >
                  {index === 1 && (
                    <span className="pill pill--now" style={{ marginBottom: 12 }}>
                      Most chosen
                    </span>
                  )}

                  <h3 style={{ fontSize: '1.3rem' }}>{plan.name}</h3>
                  {plan.description && <p style={{ color: 'var(--muted)' }}>{plan.description}</p>}

                  <div className="plan__price">
                    <span className="plan__amount">
                      {format(price!.perSeatAmount || price!.baseAmount, active)}
                    </span>
                    <span className="plan__per">
                      {price!.perSeatAmount > 0 ? 'per user' : 'flat'} /{' '}
                      {interval.toLowerCase() === 'annual' ? 'year' : 'month'}
                    </span>
                  </div>

                  <p className="plan__meta">
                    {price!.perSeatAmount > 0 && price!.baseAmount > 0
                      ? `Plus ${format(price!.baseAmount, active)} platform fee. `
                      : ''}
                    {plan.includedSeats} seat{plan.includedSeats === 1 ? '' : 's'} included
                    {plan.maximumSeats ? `, up to ${plan.maximumSeats}` : ''}.
                    {price!.taxBehavior === 'exclusive'
                      ? ` ${region.tax} added.`
                      : ` ${region.tax} included.`}
                  </p>

                  {plan.features.length > 0 && (
                    <ul className="plan__features">
                      {plan.features.slice(0, 6).map((feature) => (
                        <li key={feature.displayName}>
                          {feature.displayName}
                          {feature.limitValue != null && (
                            <span style={{ color: 'var(--muted)' }}>
                              {' '}
                              — {feature.limitValue.toLocaleString()}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="plan__cta">
                    {/* The chosen plan travels with the click, so signup does not ask again. */}
                    <Link
                      to={`/signup?plan=${encodeURIComponent(plan.planKey)}`}
                      onNavigate={navigate}
                      className="btn btn--primary btn--block"
                    >
                      {plan.isTrialAvailable ? `Start ${plan.trialDays}-day trial` : 'Get started'}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {addOns.length > 0 && (
        <section className="section section--sunken">
          <div className="wrap">
            <div className="section__head">
              <span className="eyebrow">Add-ons</span>
              <h2>Bought on top of a plan</h2>
            </div>

            <div className="grid grid--3">
              {addOns.map((plan) => (
                <article key={plan.planKey} className="card">
                  <h3>{plan.name}</h3>
                  {plan.description && <p>{plan.description}</p>}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="wrap">
          <div className="grid grid--2" style={{ gap: 48, alignItems: 'start' }}>
            <div>
              <span className="eyebrow">What happens if you stop paying</span>
              <h2>No surprises, and nothing deleted quietly</h2>
              <p style={{ color: 'var(--muted)' }}>
                A subscription moves through defined states. Suspension blocks writes according to
                policy, but your data stays recoverable for the retention period — it is not
                erased the day an invoice goes unpaid.
              </p>

              <div className="table-wrap" style={{ marginTop: 20 }}>
                <table style={{ minWidth: 0 }}>
                  <tbody>
                    {lifecycle.map((state, index) => (
                      <tr key={state}>
                        <td style={{ width: 34, color: 'var(--muted)' }}>{index + 1}</td>
                        <td>{state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <span className="eyebrow">Billing</span>
              <h2>How it is charged</h2>
              <ul className="ticks">
                <li>Monthly or annual, with proration on upgrades and downgrades</li>
                <li>Country price books, local currency and correct tax treatment</li>
                <li>Bank transfer or card, depending on what your country supports</li>
                <li>Bundles across applications while keeping module entitlements separate</li>
                <li>Promotional codes, partner discounts and negotiated pricing</li>
                <li>Onboarding, migration, training and premium support quoted separately</li>
              </ul>

              <p style={{ marginTop: 24 }}>
                <Link to="/company" onNavigate={navigate} className="btn btn--ghost">
                  Talk to sales
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function format(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unknown currency code should not take the pricing page down with it.
    return `${currency} ${amount.toLocaleString()}`;
  }
}
